import * as Portal from "@radix-ui/react-portal";
import { AnimatePresence } from "motion/react";
import { forwardRef } from "react";

import { DrawerProvider } from "./context";
import { DrawerBody } from "./DrawerBody";
import { DrawerContent } from "./DrawerContent";
import { DrawerFooter } from "./DrawerFooter";
import { DrawerHeader } from "./DrawerHeader";
import { useDrawer } from "./useDrawer";

import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { UseDrawerProps } from "./useDrawer";

export type DrawerProps = UseDrawerProps;

/**
 * Drawer component for displaying a panel that slides in from the edge of the screen.
 *
 * @example
 * // Basic usage
 * <Drawer isOpen={isOpen} onOpenChange={setIsOpen} title="Settings">
 *   <Drawer.Content>
 *     <Drawer.Header />
 *     <Drawer.Body>Content goes here</Drawer.Body>
 *     <Drawer.Footer>
 *       <Button onClick={() => setIsOpen(false)}>Close</Button>
 *     </Drawer.Footer>
 *   </Drawer.Content>
 * </Drawer>
 *
 * @example
 * // With antd-compatible props
 * <Drawer visible={visible} onClose={handleClose} placement="right" width={340} title="Edit">
 *   <Drawer.Content>
 *     <Drawer.Header />
 *     <Drawer.Body>Form content</Drawer.Body>
 *   </Drawer.Content>
 * </Drawer>
 */
export const Drawer = forwardRef<HTMLDivElement, UseDrawerProps>(({ children, ...props }, ref) => {
  const context = useDrawer(props, ref);

  const overlay = (
    <Portal.Root container={context.portalContainer as HTMLElement | undefined}>
      {children}
    </Portal.Root>
  );

  return (
    <DrawerProvider value={context}>
      <AnimatePresence>{context.isOpen ? overlay : null}</AnimatePresence>
    </DrawerProvider>
  );
}) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Content: typeof DrawerContent;
    Header: typeof DrawerHeader;
    Body: typeof DrawerBody;
    Footer: typeof DrawerFooter;
  },
  DrawerProps
>;

Drawer.displayName = "Drawer";
Drawer.Content = DrawerContent;
Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;
